import { useAuth0 } from "@auth0/auth0-react";
import { Download, Print } from "@mui/icons-material";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { useUnmountEffect } from "@react-hookz/web";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DesignHuddleEditorFrame } from "../../modules/design-huddle/design-huddle-editor-frame";
import { Editor } from "../../modules/design-huddle/types";
import { useDesignHuddleProject } from "../../modules/design-huddle/use-design-huddle-project";
import { Page } from "../../modules/layout/components/page";
import { PageContent } from "../../modules/layout/components/page-content";
import { useFlag } from "../../server/optimizely";
import { OrgUser } from "../../server/supabase/hooks/types";

const EditContentPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth0<OrgUser>();

  const [editor, setEditor] = useState<Editor>();

  const [multiFormat, , multiFormatLoaded] = useFlag("multi_format");

  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("project_id");
  const page = searchParams.get("page");

  const accountId = useMemo(() => {
    return user ? user["http://changeengine.com/account_id"] : "";
  }, [user]);

  const project = useDesignHuddleProject({
    project_id: projectId,
    account_id: accountId,
    page_number: page ? parseInt(page) : 1,
    must_be_cloned: true,
  });

  useEffect(() => {
    if (multiFormatLoaded && !multiFormat) navigate("/404");
  }, [multiFormat, multiFormatLoaded, navigate]);

  useUnmountEffect(() => editor?.remove());

  if (!multiFormatLoaded) return <CircularProgress />;

  return (
    <Page title={t("Edit Content | ChangeEngine")} sx={{ height: "100%" }}>
      {project.id ? (
        <>
          <Stack direction="row" p={2}>
            <Box flexGrow={1}>{/* TODO GRO-44 */}</Box>
            <Stack direction="row" gap={2}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Print />}
                onClick={() => alert("TODO GRO-45")}
              >
                {t("Print")}
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<Download />}
                onClick={() => alert("TODO GRO-46")}
              >
                {t("Download")}
              </Button>
            </Stack>
          </Stack>
          <DesignHuddleEditorFrame
            setEditor={setEditor}
            project_id={project.id}
            state={project.mustBeCloned ? "cloning" : project.pages ? "disabled" : "active"}
            config={{ hide_right_panel: true }}
          />
        </>
      ) : (
        <PageContent>
          <Typography variant="h1">{t("Something went wrong")}</Typography>
          <Typography variant="body1">
            {t("It's not you, it's us. Head back to the dashboard and try again.")}
          </Typography>
        </PageContent>
      )}
    </Page>
  );
};

export default EditContentPage;
